import { Breadcrumbs } from '@liveauctioneers/hammer-ui-core/breadcrumbs';
import { useCallback, useMemo } from 'react';
import { useHref, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getSEOSearchPageBreadcrumbs } from '@/redux/modules/searchPromoted.selectors';
import { addBasename, removeBasename } from '@/utils/urls';

type Props = {
    /**
     * Static trail (collection pages). Wins over the breadcrumbs the search API returns, which
     * arrive on the *promoted* search response and therefore vanish when sort !== MostRelevant.
     */
    breadcrumbs?: { href: string; label: string }[];
    className?: string;
};

export const SearchResultsBreadcrumbs = ({ breadcrumbs, className }: Props) => {
    const navigate = useNavigate();
    const rootHref = useHref('/');
    const apiBreadcrumbs = useAppSelector(getSEOSearchPageBreadcrumbs);

    // hrefs carry the basename (e.g. /en-gb) so crawlers stay on the locale's pages; navigate() adds it itself.
    const onClick = useCallback(
        (href: string) => {
            navigate(removeBasename(href, rootHref));
        },
        [navigate, rootHref]
    );

    const breadcrumbsFormatted = useMemo(
        () =>
            (
                breadcrumbs ??
                apiBreadcrumbs.map((breadcrumb) => ({
                    href: breadcrumb.url,
                    label: breadcrumb.name,
                }))
            ).map((breadcrumb) => ({ ...breadcrumb, href: addBasename(breadcrumb.href, rootHref) })),
        [breadcrumbs, apiBreadcrumbs, rootHref]
    );

    if (breadcrumbsFormatted.length === 0) {
        return null;
    }

    return (
        <Breadcrumbs
            aria-label="Search Results Breadcrumbs"
            breadcrumbs={[{ href: addBasename('/', rootHref), label: 'Home' }, ...breadcrumbsFormatted]}
            className={className}
            onClick={onClick}
        />
    );
};
